<template>
    <div class="email-preview-editor">
        <div class="d-flex justify-space-between align-center mb-1">
            <div class="caption info--text">Subject</div>
            <div v-if="isEdited" class="d-flex align-center">
                <v-chip x-small label color="accent" text-color="white" class="mr-2">Edited</v-chip>
                <v-btn
                    x-small
                    text
                    color="primary darken-1"
                    :disabled="disabled"
                    @click="resetToTemplate">
                    Reset to template
                </v-btn>
            </div>
        </div>
        <!-- Subject must stay a single line of text, so no toolbar and no Enter key -->
        <div class="email-subject-editor" @keydown.enter.prevent>
            <WysiwygEditor
                :key="`subject-${resetKey}`"
                :value="editSubject"
                :toolbar="[]"
                @input="onSubjectInput">
            </WysiwygEditor>
        </div>

        <div class="caption info--text mt-4 mb-1">Body</div>
        <WysiwygEditor
            :key="`body-${resetKey}`"
            :value="editHtml"
            :toolbar="quickNotesToolbar"
            @input="onHtmlInput">
        </WysiwygEditor>

        <div class="caption grey--text text--darken-1 mt-2">
            Edits only change this email. Leave it as-is to send the standard template.
        </div>
    </div>
</template>

<script>
    import WysiwygEditor from '../WysiwygEditor.vue'
    import { quickNotesToolbar } from './editorToolbars'

    /*  EDITABLE EMAIL PREVIEW
        Shows the template's subject & body in WysiwygEditors (only render it when a template exists). Emits `change` with
        { edited, subject, html } — subject/html are only filled in for the parts the admin changed,
        so an untouched email is sent exactly as the template builds it.
    */
    export default {
        name: 'EmailPreviewEditor',

        components: {
            WysiwygEditor
        },

        props: {
            subject: { type: String, default: '' },
            html: { type: String, default: '' },
            disabled: { type: Boolean, default: false },
            // Restores previously made edits (e.g. switching back to a buyer already edited)
            initialOverride: { type: Object, default: null }
        },

        data () {
            return {
                quickNotesToolbar,
                editSubject: '',
                editHtml: '',
                subjectChanged: false,
                htmlChanged: false,
                resetKey: 0
            }
        },

        computed: {
            isEdited () {
                return this.subjectChanged || this.htmlChanged
            }
        },

        watch: {
            // A new template (different status/type/buyer) starts from the template again
            subject: { immediate: true, handler () { this.loadTemplate() } },
            html () { this.loadTemplate() }
        },

        methods: {
            loadTemplate () {
                const override = this.initialOverride || {}
                this.editSubject = override.subject || this.subject
                this.editHtml = override.html || this.html
                this.subjectChanged = !!override.subject
                this.htmlChanged = !!override.html
                this.resetKey++
            },

            onSubjectInput (value) {
                this.editSubject = value
                this.subjectChanged = value !== this.subject
                this.emitChange()
            },

            onHtmlInput (value) {
                this.editHtml = value
                this.htmlChanged = value !== this.html
                this.emitChange()
            },

            resetToTemplate () {
                this.editSubject = this.subject
                this.editHtml = this.html
                this.subjectChanged = false
                this.htmlChanged = false
                this.resetKey++
                this.emitChange()
            },

            emitChange () {
                this.$emit('change', {
                    edited: this.isEdited,
                    subject: this.subjectChanged ? this.editSubject : '',
                    html: this.htmlChanged ? this.editHtml : ''
                })
            }
        }
    }
</script>

<style scoped>
    .email-subject-editor >>> .wysiwyg-editor__content {
        min-height: auto !important;
    }
</style>



// WEBPACK FOOTER //
// src/components/shared/EmailPreviewEditor.vue